import React from 'react';
import { Alert, Box, Button, ButtonBase, Chip, Paper, Skeleton, Snackbar, Tab, Tabs, Typography } from '@mui/material';
import { Link as RouterLink, useParams, useSearchParams } from 'react-router-dom';
import { DataGate } from '../BotOpsData';
import { useBotDetail } from '../hooks/useBotDetail';
import { Bot, Snapshot, SnapshotMeta } from '../lib/types';
import { ink, tint } from '../lib/tone';
import { ActionRow, STATUS_LABEL, actionRows, botBySlug, findAction, unreadByBot } from '../lib/selectors';
import { botKpis, recRows } from '../lib/botPage';
import { fmtIct } from '../lib/ict';
import { ProvLine } from '../chips';
import { StatusDot } from '../StatusDot';
import { ACCENT, FONT } from '../lib/brand';
import { ActionCenter } from '../ActionCenter';
import { DecisionControl } from '../DecisionControl';
import { InboxPanel, QuestionPanel } from '../InboxPanel';
import { RecommendationDrawer } from '../RecommendationDrawer';
import { KpiStrip } from './KpiStrip';
import { OverviewTab } from './OverviewTab';
import { JdView } from './JdView';
import { WorkTab } from './WorkTab';
import { RecommendationsTab } from './RecommendationsTab';
import { DocumentsTab } from './DocumentsTab';

export const BOT_TABS = ['overview', 'jd', 'work', 'recommendations', 'documents', 'inbox', 'actions'] as const;
export type BotTab = (typeof BOT_TABS)[number];
const TAB_LABEL: Record<BotTab, string> = { overview: 'Overview', jd: 'Job description', work: 'Current work', recommendations: 'Recommendations', documents: 'Documents', inbox: 'Inbox', actions: 'Action Center' };

const BASE = '/dashboard/bot-ops';

function NotFound({ slug }: { slug: string }) {
  return (
    <Paper variant="outlined" data-testid="bot-404" sx={{ p: 3, maxWidth: 640, mx: 'auto', mt: 4 }}>
      <Typography component="h1" sx={{ fontFamily: FONT.display, fontSize: 20, fontWeight: 700, color: 'primary.main', mb: 1 }}>No bot called “{slug}”</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>That bot is not in the latest snapshot. It may have been renamed or removed.</Typography>
      <Button component={RouterLink} to={BASE} variant="contained">Back to Grokbot</Button>
    </Paper>
  );
}

function Inner({ slug, snapshot, meta, canWrite }: { slug: string; snapshot: Snapshot; meta: SnapshotMeta | null; canWrite: boolean }) {
  const [sp, setSp] = useSearchParams();
  const [toast, setToast] = React.useState<string | null>(null);
  const bot = botBySlug(snapshot, slug);
  const { detail, loading, error, notFound, reload } = useBotDetail(slug, meta ? meta.sha256 : null);

  const rawTab = sp.get('tab');
  const tab: BotTab = (BOT_TABS as readonly string[]).includes(rawTab || '') ? (rawTab as BotTab) : 'overview';
  const recId = sp.get('rec');
  const actionId = sp.get('action');
  const instructionId = sp.get('instruction');

  const update = (updates: Record<string, string | null>, push = false) => {
    setSp((prev) => {
      const next = new URLSearchParams(prev);
      Object.entries(updates).forEach(([k, v]) => { if (v === null || v === '') next.delete(k); else next.set(k, v); });
      if (next.get('tab') === 'overview') next.delete('tab');
      return next;
    }, { replace: !push });
  };

  const rows = React.useMemo(() => (bot ? recRows(bot, { recommendations: detail ? detail.recommendations : [] }) : []), [bot, detail]);

  if (!bot || (notFound && !detail)) return <NotFound slug={slug} />;

  const merged: Bot = detail ? {
    ...bot,
    jd_markdown: detail.jd_markdown, jd_markdown_path: detail.jd_markdown_path, jd_sections: detail.jd_sections, jd_prov: detail.jd_prov,
    tasks: detail.tasks, recommendations: detail.recommendations, documents: detail.documents, activity: detail.activity || bot.activity, kpis: detail.kpis || bot.kpis,
  } : bot;
  const ready = Boolean(detail);
  const kpis = botKpis(rows);
  const trend = ((merged.kpis as { trend?: { date: string; new_recommendations: number }[] } | null) || {}).trend || [];
  const unread = unreadByBot(snapshot)[slug] || 0;
  const accent = ACCENT[bot.company];
  const item: ActionRow | null = actionId ? findAction(snapshot, actionId) : null;
  const docs = detail ? detail.documents || [] : [];

  const copyLink = async () => {
    try { await navigator.clipboard.writeText(window.location.href); setToast('Link copied'); } catch { setToast('Could not copy. The address bar has the link.'); }
  };
  const goTab = (t: string) => update({ tab: t, rec: t === 'recommendations' ? recId : null }, true);
  const openInstruction = (id: string) => update({ tab: 'inbox', instruction: id, action: null }, true);

  const detailGate = (render: () => React.ReactNode) => {
    if (ready) return render();
    if (error && !loading) {
      return (
        <Alert severity="error" role="alert" action={<Button color="inherit" size="small" onClick={reload}>Retry</Button>} data-testid="detail-error">
          Could not load this bot&apos;s detail: {error.message}
        </Alert>
      );
    }
    return <Skeleton variant="rounded" height={160} data-testid="detail-loading" />;
  };

  return (
    <Box sx={{ maxWidth: 1480, mx: 'auto', p: { xs: 1.5, md: 3 } }}>
      <ButtonBase component={RouterLink} to={`${BASE}${bot.company === 'quill' ? '?company=quill' : ''}`} sx={{ fontSize: 12.5, color: 'primary.main', mb: 1, textDecoration: 'underline' }}>← Grokbot</ButtonBase>

      <Box component="header" sx={{ borderLeft: `6px solid ${accent.acc}`, pl: 1.6, mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <StatusDot color={bot.color} size={14} />
          <Typography component="h1" sx={{ fontFamily: FONT.display, fontSize: 24, fontWeight: 700, color: 'primary.main' }}>{bot.name}</Typography>
          <Chip size="small" label={bot.company === 'quill' ? 'Quill' : 'LifeScribe'} sx={{ bgcolor: tint(accent.soft), color: ink(accent.ink), fontWeight: 700 }} />
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.4 }}>
          {bot.role_title || 'Role not stated in source'} · reports to {bot.reports_to_label || bot.reports_to || 'not stated'} · {STATUS_LABEL[bot.color]} · last active {fmtIct(bot.last_activity_ict)}
        </Typography>
        <Typography sx={{ fontSize: 14, mt: 1, maxWidth: 900 }} data-testid="bot-profile">{bot.profile_description || 'No plain-language description in the bot files yet.'}</Typography>
        {bot.profile_prov && <ProvLine prov={bot.profile_prov} fmt={fmtIct} />}
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1.2 }}>
          <Button size="small" variant="contained" onClick={() => update({ tab: 'inbox' }, true)}>Instruct · Ask a question{unread ? ` (${unread})` : ''}</Button>
          <Button size="small" variant="outlined" onClick={copyLink}>Copy link</Button>
          <Button size="small" variant="outlined" component={RouterLink} to={`${BASE}?${bot.company === 'quill' ? 'company=quill&' : ''}bot=${encodeURIComponent(bot.slug)}&tab=inbox`}>Quick preview</Button>
        </Box>
      </Box>

      {ready ? <KpiStrip k={kpis} trend={trend} /> : <Skeleton variant="rounded" height={92} sx={{ mb: 2 }} data-testid="kpi-loading" />}

      <Tabs
        value={tab}
        onChange={(_e, v: BotTab) => goTab(v)}
        variant="scrollable"
        scrollButtons="auto"
        aria-label="Bot sections"
        sx={{ borderBottom: '1px solid', borderColor: 'divider', mb: 2, '& .MuiTab-root': { textTransform: 'none', fontWeight: 600 } }}
      >
        {BOT_TABS.map((t) => {
          const n = t === 'recommendations' && ready ? rows.length : t === 'documents' && ready ? docs.length : t === 'actions' ? (bot.actions || []).length : t === 'inbox' && unread ? unread : null;
          return <Tab key={t} value={t} label={n == null ? TAB_LABEL[t] : `${TAB_LABEL[t]} (${n})`} />;
        })}
      </Tabs>

      <Box role="tabpanel" data-testid={`panel-${tab}`}>
        {tab === 'overview' && (
          <OverviewTab bot={merged} rows={rows} docs={docs} detailReady={ready} onOpenRec={(id) => update({ tab: 'recommendations', rec: id }, true)} onTab={goTab} />
        )}
        {tab === 'jd' && detailGate(() => <JdView bot={merged} />)}
        {tab === 'work' && <WorkTab bot={merged} />}
        {tab === 'recommendations' && detailGate(() => (
          <RecommendationsTab bot={bot} snapshot={snapshot} rows={rows} expandedId={recId} onExpand={(id) => update({ rec: id })} onOpenInstruction={openInstruction} />
        ))}
        {tab === 'documents' && detailGate(() => <DocumentsTab bot={bot} docs={docs} />)}
        {tab === 'inbox' && (
          <Box sx={{ maxWidth: 760 }}><InboxPanel snapshot={snapshot} bot={bot} openId={instructionId} focusComposer={sp.get('compose') === '1'} /></Box>
        )}
        {tab === 'actions' && (
          <ActionCenter
            snapshot={snapshot}
            company={bot.company}
            rows={actionRows(snapshot, { company: bot.company, bot: slug })}
            allRowsForCounts={actionRows(snapshot, { company: bot.company, bot: slug })}
            decision={(sp.get('decision') as never) || 'all'}
            onDecisionFilter={(d) => update({ decision: d === 'all' ? null : d })}
            selectedBot={bot}
            onClearBot={() => goTab('overview')}
            onInstructBot={() => update({ tab: 'inbox' }, true)}
            onOpenBotDetail={() => update({ tab: 'overview' }, true)}
            highlightId={null}
            onOpenAction={(id) => update({ action: id }, true)}
            onOpenBot={() => update({ tab: 'overview' }, true)}
            onGa={() => setToast('Grant asks are on the main Grokbot page.')}
            unread={unreadByBot(snapshot)}
            renderDecision={(p) => <DecisionControl row={p.row} snapshot={snapshot} onOpenInstruction={openInstruction} />}
          />
        )}
      </Box>
      {tab === 'actions' && (
        <Box sx={{ mt: 1 }}><Button component={RouterLink} size="small" to={`${BASE}?${bot.company === 'quill' ? 'company=quill&' : ''}bot=${encodeURIComponent(slug)}`}>Open in main Action Center</Button></Box>
      )}

      <RecommendationDrawer
        row={item}
        company={bot.company}
        onClose={() => update({ action: null })}
        onOpenBot={() => update({ action: null, tab: 'overview' })}
        onShowInActionCenter={() => update({ action: null, tab: 'actions' })}
        onCopyLink={copyLink}
        onGa={() => { update({ action: null }); setToast('Grant asks are on the main Grokbot page.'); }}
        renderDecision={(r) => <DecisionControl row={r} snapshot={snapshot} onOpenInstruction={openInstruction} layout="drawer" />}
        renderQuestion={(r) => <QuestionPanel snapshot={snapshot} row={r} openId={instructionId} />}
      />

      {!canWrite && <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 3 }}>Read-only: only the super admin can send instructions or decide.</Typography>}
      <Snackbar open={Boolean(toast)} autoHideDuration={4000} onClose={() => setToast(null)} message={toast} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }} />
    </Box>
  );
}

/** /dashboard/bot-ops/bots/:slug */
export default function BotPage() {
  const { slug = '' } = useParams();
  return (
    <DataGate>
      {({ snapshot, meta, canWrite }) => <Inner slug={slug} snapshot={snapshot} meta={meta} canWrite={canWrite} />}
    </DataGate>
  );
}
